import PropTypes from "prop-types";
import { ArrowDown, ArrowUp, ArrowUpDown } from "lucide-react";
import { cn } from "@/lib/utils";
import { ContactActionMenu } from "@/components/people/ContactActionMenu";
import {
  avatarTone,
  channelBadgeTone,
  contactStatus,
  enrichPerson,
  fmtAgo,
} from "@/pages/people/contactMasterUtils";

function StatusPill({ status }) {
  const active = status === "active";
  return (
    <span
      className={cn(
        "inline-flex items-center rounded-full px-2 py-0.5 text-[11px] font-semibold",
        active
          ? "bg-emerald-50 text-emerald-700 ring-1 ring-inset ring-emerald-100"
          : "bg-slate-100 text-slate-500 ring-1 ring-inset ring-slate-200",
      )}
    >
      {active ? "Active" : "Inactive"}
    </span>
  );
}

StatusPill.propTypes = {
  status: PropTypes.oneOf(["active", "inactive"]).isRequired,
};

const COLUMNS = [
  { key: "name", label: "Contact", sortable: true },
  { key: "email", label: "Email", sortable: true },
  { key: "phone", label: "Phone", sortable: true },
  { key: "channel", label: "Channel", sortable: true },
  { key: "conversations", label: "Conversations", sortable: true },
  { key: "last_touch", label: "Last Activity", sortable: true },
  { key: "status", label: "Status", sortable: false },
  { key: "actions", label: "", sortable: false },
];

function _getAriaSort(col, active, sortOrder) {
  if (!col.sortable) return undefined;
  if (!active) return "none";
  return sortOrder === "asc" ? "ascending" : "descending";
}

function _renderColumnHeader(col, sortKey, sortOrder, handleSort) {
  const active = col.sortable && sortKey === col.key;
  const ariaSort = _getAriaSort(col, active, sortOrder);


  return (
    <th
      key={col.key}
      className={cn(
        "px-4 py-3 text-[10.5px] font-semibold uppercase tracking-wide text-slate-400",
        col.key === "actions" && "text-right",
        col.sortable && "cursor-pointer select-none hover:text-slate-700",
        active && "text-indigo-700",
      )}
      onClick={col.sortable ? () => handleSort(col.key) : undefined}
      aria-sort={ariaSort}
      data-testid={col.sortable ? `people-sort-${col.key}` : undefined}
    >
      <span className={cn(
        "inline-flex items-center gap-1",
        col.key === "actions" && "justify-end w-full",
      )}
      >
        {col.label}
        {col.sortable ? <SortIcon active={active} order={sortOrder} /> : null}
      </span>
    </th>
  );
}

function SortIcon({ active, order }) {
  if (!active) return <ArrowUpDown size={12} className="opacity-40" />;
  if (order === "asc") return <ArrowUp size={12} />;
  return <ArrowDown size={12} />;
}

SortIcon.propTypes = {
  active: PropTypes.bool,
  order: PropTypes.oneOf(["asc", "desc"]),
};

function ContactTableRow({
  person: p,
  status,
  convLabel,
  onViewHistory,
  onEdit,
  onToggleInactive,
  onDelete,
}) {
  return (
    <tr
      className="border-b border-slate-100 last:border-0 hover:bg-slate-50/70 transition-colors"
      data-testid={`person-row-${p.id}`}
    >
      <td className="px-4 py-3">
        <button
          type="button"
          className="flex items-center gap-3 min-w-0 text-left"
          onClick={() => onViewHistory(p)}
        >
          <div
            className={cn(
              "flex h-9 w-9 shrink-0 items-center justify-center rounded-full text-[11px] font-semibold text-white",
              avatarTone(p.channel),
            )}
          >
            {p.initials || "??"}
          </div>
          <div className="min-w-0">
            <div className="truncate text-[13px] font-semibold text-slate-900">
              {p.name}
            </div>
            <div className="truncate text-[11px] text-slate-400">
              {p.channel_label || p.channel}
            </div>
          </div>
        </button>
      </td>
      <td className="px-4 py-3 text-[12.5px] text-slate-600 max-w-[200px] truncate">
        {p.email || "—"}
      </td>
      <td className="px-4 py-3 text-[12.5px] text-slate-600 whitespace-nowrap">
        {p.phone || "—"}
      </td>
      <td className="px-4 py-3">
        <span
          className={cn(
            "inline-flex items-center rounded-md px-2 py-0.5 text-[11px] font-medium ring-1 ring-inset",
            channelBadgeTone(p.channel),
          )}
        >
          {p.channel_label || p.channel}
        </span>
      </td>
      <td className="px-4 py-3 text-[12.5px] text-slate-600 whitespace-nowrap">
        {convLabel}
      </td>
      <td className="px-4 py-3 text-[12.5px] text-slate-500 whitespace-nowrap">
        {fmtAgo(p.last_touch_at)}
      </td>
      <td className="px-4 py-3">
        <StatusPill status={status} />
      </td>
      <td className="px-4 py-3 text-right">
        <ContactActionMenu
          status={status}
          onViewHistory={() => onViewHistory(p)}
          onEdit={() => onEdit(p)}
          onToggleInactive={() => onToggleInactive(p)}
          onDelete={() => onDelete(p)}
        />
      </td>
    </tr>
  );
}

ContactTableRow.propTypes = {
  person: PropTypes.object.isRequired,
  status: PropTypes.string.isRequired,
  convLabel: PropTypes.string.isRequired,
  onViewHistory: PropTypes.func.isRequired,
  onEdit: PropTypes.func.isRequired,
  onToggleInactive: PropTypes.func.isRequired,
  onDelete: PropTypes.func.isRequired,
};

export function ContactTable({
  people,
  inactiveIds,
  sortKey = "last_touch",
  sortOrder = "desc",
  onSortChange,
  onViewHistory,
  onEdit,
  onToggleInactive,
  onDelete,
}) {
  const handleSort = (key) => {
    if (!onSortChange) return;
    if (sortKey === key) {
      onSortChange(key, sortOrder === "asc" ? "desc" : "asc");
      return;
    }
    onSortChange(key, key === "name" || key === "email" || key === "channel" ? "asc" : "desc");
  };

  return (
    <div
      className="min-w-0 overflow-x-auto rounded-xl border border-slate-200 bg-white"
      data-testid="contact-table"
    >
      <table className="w-full min-w-[920px] border-collapse text-left">
        <thead>
          <tr className="border-b border-slate-100 bg-slate-50/60">
            {COLUMNS.map((col) => _renderColumnHeader(col, sortKey, sortOrder, handleSort))}
          </tr>
        </thead>
        <tbody>
          {people.map((raw) => {
            const p = enrichPerson(raw);
            const status = contactStatus(p, inactiveIds);
            const convLabel = `${p.conversation_count || 0} convo${p.conversation_count === 1 ? "" : "s"}`;
            return (
              <ContactTableRow
                key={p.id}
                person={p}
                status={status}
                convLabel={convLabel}
                onViewHistory={onViewHistory}
                onEdit={onEdit}
                onToggleInactive={onToggleInactive}
                onDelete={onDelete}
              />
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

ContactTable.propTypes = {
  people: PropTypes.arrayOf(PropTypes.object).isRequired,
  inactiveIds: PropTypes.instanceOf(Set),
  sortKey: PropTypes.string,
  sortOrder: PropTypes.oneOf(["asc", "desc"]),
  onSortChange: PropTypes.func,
  onViewHistory: PropTypes.func.isRequired,
  onEdit: PropTypes.func.isRequired,
  onToggleInactive: PropTypes.func.isRequired,
  onDelete: PropTypes.func.isRequired,
};
